
import React from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';
import Button from '../../../components/base/Button';

export default function DebtSettlement() {
  const navigate = useNavigate();

  const benefits = [
    {
      icon: 'ri-calculator-line',
      title: 'Debt Assessment Tools',
      description: 'Interactive calculators that help prospects understand potential savings and payment reductions.'
    },
    {
      icon: 'ri-user-search-line',
      title: 'Financial Hardship Targeting',
      description: 'Advanced targeting to reach individuals facing financial difficulties and seeking debt relief.'
    },
    {
      icon: 'ri-heart-pulse-line',
      title: 'Credit Repair Integration',
      description: 'Combined approach addressing both debt settlement and credit score improvement strategies.'
    },
    {
      icon: 'ri-shield-line',
      title: 'Compliance Focused',
      description: 'All campaigns follow strict FDCPA and state regulations for debt settlement advertising.'
    }
  ];

  const process = [
    {
      step: '01',
      title: 'Audience Analysis',
      description: 'Identify individuals with high debt loads, financial stress, and settlement potential.'
    },
    {
      step: '02',
      title: 'Campaign Creation',
      description: 'Develop empathetic messaging focused on financial relief and debt reduction benefits.'
    },
    {
      step: '03',
      title: 'Lead Qualification',
      description: 'Multi-step qualification process to verify debt amounts, hardship status, and settlement eligibility.'
    },
    {
      step: '04',
      title: 'Conversion Optimization',
      description: 'Continuous optimization based on enrollment rates and client feedback.'
    }
  ];

  const stats = [
    { number: '425K+', label: 'Debt Settlement Leads' },
    { number: '22%', label: 'Average Conversion Rate' },
    { number: '$165', label: 'Average Cost Per Lead' },
    { number: '68%', label: 'Enrollment Rate' }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section 
        className="relative bg-cover bg-center bg-no-repeat py-32"
        style={{
          backgroundImage: `linear-gradient(rgba(236, 72, 153, 0.8), rgba(219, 39, 119, 0.8)), url('https://readdy.ai/api/search-image?query=Compassionate%20debt%20settlement%20advisor%20helping%20stressed%20family%20with%20financial%20documents%20in%20comfortable%20consultation%20room%2C%20showing%20debt%20reduction%20charts%20and%20payment%20plans%20on%20tablet%2C%20supportive%20professional%20environment&width=1920&height=800&seq=debt-settlement-hero&orientation=landscape')`
        }}
      >
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-4xl">
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              Debt Settlement Lead Generation
            </h1>
            <p className="text-xl md:text-2xl text-pink-100 mb-8">
              Generate qualified leads for debt consolidation and settlement services, targeting individuals seeking financial relief, debt reduction strategies, and improved payment terms.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button 
                size="lg"
                onClick={() => navigate('/contact')}
                className="bg-white text-black hover:bg-gray-100 font-semibold"
              >
                <i className="ri-phone-line mr-2 text-black"></i>
                Start Generating Leads
              </Button>
              <Button 
                variant="outline"
                size="lg"
                onClick={() => navigate('/case-studies')}
                className="border-white text-white hover:bg-white hover:text-pink-600 font-semibold"
              >
                <i className="ri-bar-chart-line mr-2"></i>
                Success Stories
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Debt Settlement Lead Performance
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our specialized approach connects debt settlement companies with qualified prospects seeking financial relief.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="bg-white p-8 rounded-2xl shadow-lg text-center">
                <div className="text-4xl font-bold text-pink-600 mb-2">{stat.number}</div>
                <div className="text-gray-600 font-medium">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Specialized Debt Settlement Marketing
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We understand the sensitive nature of debt settlement and create campaigns that build trust and provide hope.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <div key={index} className="text-center">
                <div className="w-20 h-20 flex items-center justify-center bg-pink-100 rounded-full mx-auto mb-6">
                  <i className={`${benefit.icon} text-3xl text-pink-600`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">{benefit.title}</h3>
                <p className="text-gray-600 leading-relaxed">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-pink-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Our Debt Settlement Lead Process
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              A compassionate approach that respects the financial struggles of prospects while delivering qualified leads.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {process.map((step, index) => (
              <div key={index} className="bg-white p-8 rounded-2xl shadow-lg text-center">
                <div className="w-16 h-16 flex items-center justify-center bg-pink-600 text-white text-2xl font-bold rounded-full mx-auto mb-6">
                  {step.step}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">{step.title}</h3>
                <p className="text-gray-600 leading-relaxed">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col lg:flex-row items-center gap-12">
            <div className="lg:w-1/2">
              <img 
                src="https://readdy.ai/api/search-image?query=Professional%20debt%20counselor%20meeting%20with%20concerned%20client%20reviewing%20financial%20statements%20and%20debt%20consolidation%20options%20in%20warm%20office%20setting%2C%20calculator%20and%20payment%20plan%20documents%20on%20desk%2C%20empathetic%20consultation%20atmosphere&width=600&height=400&seq=debt-consultation&orientation=landscape"
                alt="Debt Settlement Consultation"
                className="w-full rounded-2xl shadow-lg object-cover object-top"
              />
            </div>
            <div className="lg:w-1/2">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                Ethical Debt Settlement Marketing
              </h2>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                Our campaigns focus on providing genuine help to individuals facing financial hardship, building trust through transparent communication and realistic expectations.
              </p>
              <div className="space-y-4">
                <div className="flex items-center">
                  <div className="w-8 h-8 flex items-center justify-center bg-pink-100 rounded-lg mr-4">
                    <i className="ri-check-line text-pink-600"></i>
                  </div>
                  <span className="text-gray-700">FDCPA compliant advertising and messaging</span>
                </div>
                <div className="flex items-center">
                  <div className="w-8 h-8 flex items-center justify-center bg-pink-100 rounded-lg mr-4">
                    <i className="ri-check-line text-pink-600"></i>
                  </div>
                  <span className="text-gray-700">Debt-to-income ratio qualification</span>
                </div>
                <div className="flex items-center">
                  <div className="w-8 h-8 flex items-center justify-center bg-pink-100 rounded-lg mr-4">
                    <i className="ri-check-line text-pink-600"></i>
                  </div>
                  <span className="text-gray-700">Financial hardship verification</span>
                </div>
                <div className="flex items-center">
                  <div className="w-8 h-8 flex items-center justify-center bg-pink-100 rounded-lg mr-4">
                    <i className="ri-check-line text-pink-600"></i>
                  </div>
                  <span className="text-gray-700">Multi-channel lead nurturing campaigns</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-pink-600">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Help More People Find Debt Relief
          </h2>
          <p className="text-xl text-pink-100 mb-8">
            Partner with us to connect with individuals seeking legitimate debt settlement solutions and financial freedom.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="tel:+910120418794"
              className="inline-flex items-center justify-center px-6 py-3 bg-white text-black hover:bg-gray-100 rounded-lg font-semibold transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-phone-line mr-2"></i>
              Call +91 0120 418 7944
            </a>
            <Button 
              variant="outline"
              size="lg"
              onClick={() => navigate('/services')}
              className="border-white text-white hover:bg-white hover:text-pink-600"
            >
              <i className="ri-arrow-left-line mr-2"></i>
              All Services
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
